/**
 * OCP-812 - "run the managed backup now", the tenant's ops key and the re-run
 * history, for the tenant's managed-backup panel. Same REST routes as
 * `ocp_run_tenant_backup` / `ocp_get_tenant_backup_run` /
 * `ocp_rotate_tenant_ops_key` / `ocp_revoke_tenant_ops_key`: super_admin with
 * a verified second factor, a reason, audited with source admin-ui. The ops
 * key itself is never part of any payload.
 */
import { useCallback, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

/** OCP-812 - one managed-backup re-run OCP triggered on the tenant. */
export interface BackupRerun {
  id: string;
  trigger: string;
  attempt: number;
  slot_at: string | null;
  status: string;
  reason: string;
  requested_by: string;
  source: string;
  scheduled_for: string | null;
  requested_at: string | null;
  http_status: number | null;
  run_id: string | null;
  run_started_at: string | null;
  run_finished_at: string | null;
  error: string | null;
  created_at: string;
}

interface RunResponse {
  rerun: BackupRerun;
  error?: string;
  message?: string | null;
}

interface RunStatusResponse {
  run: { runId: string; status: 'running' | 'success' | 'failed'; startedAt: string | null; finishedAt: string | null; error: string | null } | null;
  status: string;
}

/** Status reads after "Run backup now": every 10 s, at most 30 (core allows 60 per hour). */
export const RUN_POLL_MS = 10_000;
const RUN_POLL_MAX = 30;

function errorText(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Failed.';
  const body = err.body as { error?: string; message?: string | null; rerun?: BackupRerun } | null;
  const code = body?.error ?? '';
  if (code === 'second_factor_required' || code === 'mfa_enrollment_required') return 'Needs a super_admin session with a verified second factor.';
  if (code === 'forbidden_for_role') return 'Only a super_admin may do this.';
  const detail = body?.message ?? body?.rerun?.error ?? null;
  return `Failed (HTTP ${err.status}${code ? `, ${code}` : ''})${detail ? `: ${detail}` : ''}`;
}

/**
 * Run the tenant's managed backup now - the first action on a failed-backup
 * alert. Shows the run status until it finished.
 */
export function RunBackupNow({ tenantId, keyConfigured, pollMs = RUN_POLL_MS }: { tenantId: string; keyConfigured: boolean; pollMs?: number }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 200;

  const poll = useCallback(
    async (runId: string) => {
      for (let i = 0; i < RUN_POLL_MAX; i++) {
        await new Promise((r) => setTimeout(r, pollMs));
        try {
          const res = await api<RunStatusResponse>(`/v1/admin/tenants/${tenantId}/backups/runs/${runId}`);
          if (res.run?.status === 'success') {
            setStatus(`Run ${runId} succeeded at ${res.run.finishedAt ? fmtDateTime(res.run.finishedAt) : 'now'}.`);
            return;
          }
          if (res.run?.status === 'failed') {
            setStatus(`Run ${runId} FAILED${res.run.error ? `: ${res.run.error}` : ''}.`);
            return;
          }
          setStatus(`Run ${runId} is running (started ${res.run?.startedAt ? fmtDateTime(res.run.startedAt) : '?'})...`);
        } catch (err) {
          setError(errorText(err));
          return;
        }
      }
      setStatus(`Run ${runId} is still running - check again later.`);
    },
    [tenantId, pollMs],
  );

  async function run() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const res = await api<RunResponse>(`/v1/admin/tenants/${tenantId}/backups/run`, { method: 'POST', body: { reason: trimmed } });
      const r = res.rerun;
      if (r.status === 'success') setStatus(`Run ${r.run_id} succeeded.`);
      else if (r.status === 'failed') setStatus(`Run ${r.run_id ?? ''} FAILED${r.error ? `: ${r.error}` : ''}.`);
      else if (r.run_id) {
        setStatus(`Run ${r.run_id} started - waiting for the result...`);
        void poll(r.run_id);
      } else setStatus(`Status: ${r.status}${r.error ? ` - ${r.error}` : ''}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 p-3 text-xs" data-testid="backup-run-now">
      <div className="font-medium text-gray-700 dark:text-gray-300">Run backup now</div>
      <p className="mt-1 text-gray-500">
        Runs the tenant&apos;s managed backup immediately instead of waiting for the next hourly slot. OCP already re-runs a
        failed slot on its own (5 minutes later, at most twice). Super_admin with a second factor; the reason is audited
        here and in the tenant&apos;s own audit log.
      </p>
      {!keyConfigured && (
        <p className="mt-1 text-amber-700 dark:text-amber-300" data-testid="backup-run-now-no-key">
          No ops key - the run is refused until the tenant has one (redeploy, or issue it under &quot;Ops key&quot;).
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          placeholder="reason (required, audited)"
          aria-label="backup run reason"
          className="min-w-[16rem] flex-1 rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1"
        />
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => void run()}
          className="rounded bg-brand-500 px-2 py-1 text-white disabled:opacity-50"
          data-testid="backup-run-now-submit"
        >
          {busy ? 'Starting...' : 'Run backup now'}
        </button>
      </div>
      {status && (
        <div className="mt-1 text-gray-700 dark:text-gray-300" data-testid="backup-run-now-status">
          {status}
        </div>
      )}
      {error && (
        <div className="mt-1 text-red-600" data-testid="backup-run-now-error">
          {error}
        </div>
      )}
    </div>
  );
}

/** Rotate (also lifts a revocation) or revoke the tenant's ops key. */
export function OpsKeyActions({ tenantId, configured }: { tenantId: string; configured: boolean }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [redeploy, setRedeploy] = useState<'now' | 'next_roll' | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The redeploy choice is required: no default, so the operator decides knowingly.
  const valid = reason.trim().length >= 10 && redeploy !== null;
  async function act(action: 'rotate' | 'revoke') {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ applies_on: string; redeploy_required: boolean }>(`/v1/admin/tenants/${tenantId}/ops-key/${action}`, {
        method: 'POST',
        body: { reason: reason.trim(), redeploy },
      });
      setResult(`${action === 'rotate' ? 'Rotated' : 'Revoked'} - ${res.applies_on}.${res.redeploy_required ? ' Redeploy required.' : ''}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-1 text-xs">
      <button type="button" className="text-brand-500 hover:underline" onClick={() => setOpen((v) => !v)} data-testid="ops-key-toggle">
        {open ? 'hide' : configured ? 'rotate / revoke' : 'issue'}
      </button>
      {open && (
        <div className="mt-1 grid gap-1">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="reason (audited, at least 10 characters)"
            aria-label="ops key reason"
            className="rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1"
          />
          <fieldset className="grid gap-0.5" data-testid="ops-key-redeploy">
            <legend className="text-gray-600 dark:text-gray-400">Deliver it to the tenant instance</legend>
            <label className="flex items-start gap-1">
              <input type="radio" name={`ops-key-redeploy-${tenantId}`} checked={redeploy === 'now'} onChange={() => setRedeploy('now')} data-testid="ops-key-redeploy-now" />
              <span>Redeploy now - a few minutes of tenant downtime; the change is on the instance afterwards</span>
            </label>
            <label className="flex items-start gap-1">
              <input type="radio" name={`ops-key-redeploy-${tenantId}`} checked={redeploy === 'next_roll'} onChange={() => setRedeploy('next_roll')} data-testid="ops-key-redeploy-next-roll" />
              <span>At the next redeploy or maintenance roll - until then the instance keeps the old key (a leaked key keeps working there) and the alert ops-key-pending stays open</span>
            </label>
          </fieldset>
          <div className="flex gap-2">
            <button type="button" disabled={!valid || busy} onClick={() => void act('rotate')} className="rounded bg-brand-500 px-2 py-1 text-white disabled:opacity-50" data-testid="ops-key-rotate">
              {configured ? 'Rotate ops key' : 'Issue ops key'}
            </button>
            {configured && (
              <button type="button" disabled={!valid || busy} onClick={() => void act('revoke')} className="rounded border border-red-300 px-2 py-1 text-red-700 disabled:opacity-50" data-testid="ops-key-revoke">
                Revoke
              </button>
            )}
          </div>
          {result && (
            <span className="text-gray-600 dark:text-gray-400" data-testid="ops-key-result">
              {result}
            </span>
          )}
          {error && (
            <span className="text-red-600" data-testid="ops-key-error">
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** The latest re-runs: what was retried, by whom, and the result. */
export function RerunHistory({ reruns }: { reruns: BackupRerun[] }) {
  return (
    <div className="mt-4 text-xs" data-testid="backup-rerun-history">
      <div className="font-medium text-gray-700 dark:text-gray-300">Re-runs</div>
      <table className="mt-1 w-full text-left">
        <thead className="text-gray-500">
          <tr>
            <th className="py-1 pr-2 font-normal">When</th>
            <th className="py-1 pr-2 font-normal">Trigger</th>
            <th className="py-1 pr-2 font-normal">Status</th>
            <th className="py-1 font-normal">Detail</th>
          </tr>
        </thead>
        <tbody>
          {reruns.map((r) => (
            <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800 align-top" data-testid="backup-rerun-row">
              <td className="py-1 pr-2 whitespace-nowrap">{fmtDateTime(r.requested_at ?? r.scheduled_for ?? r.created_at)}</td>
              <td className="py-1 pr-2">
                {r.trigger === 'automatic_retry'
                  ? `automatic ${r.attempt}/2${r.slot_at ? ` (slot ${fmtDateTime(r.slot_at)})` : ''}`
                  : `operator (${r.requested_by}, ${r.source})`}
              </td>
              <td
                className={`py-1 pr-2 font-medium ${
                  r.status === 'success'
                    ? 'text-green-700 dark:text-green-400'
                    : r.status === 'running' || r.status === 'scheduled'
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-red-700 dark:text-red-400'
                }`}
              >
                {r.status}
              </td>
              <td className="py-1 break-words text-gray-600 dark:text-gray-400">{r.error ?? r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
